import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LoaderWidget, SmartTooltip, useRootContext } from '@jarvis/react-portal-addons';
import { Button, IconCheckmarkCircle } from '@veneer/core';
import primitives from '@veneer/primitives';
import { publishEvent } from '../../../../utils/analytics';
import {
  CardBody,
  CardContainer,
  ContentContainer,
  Description,
  Footer,
  HeaderContainer,
  HeaderText,
  ImageContainer,
  Title,
} from './styles';

const SolutionCardTemplate = ({
  analyticsData,
  headerText,
  title,
  description,
  icon,
  footerText,
  footerHyperlink,
  onClick = null,
  dataTestIds,
  showCheckmarkCircleIcon = false,
  loading,
}) => {
  const { shell } = useRootContext() || {};

  const onClickHandler = e => {
    if (analyticsData) {
      publishEvent(analyticsData);
    }

    if (onClick) {
      e.preventDefault();
      onClick(e);
    } else if (footerHyperlink.startsWith('http://') || footerHyperlink.startsWith('https://')) {
      window?.open(footerHyperlink, '_blank', 'noopener,noreferrer');
    } else {
      shell?.navigation?.push(footerHyperlink);
    }
  };

  const headerTextComponent = useMemo(() => (
    <SmartTooltip
      id={`${dataTestIds.header}-tooltip`}
      content={headerText
        .replaceAll('{{tagReplaceStart}}', '')
        .replaceAll('{{tagReplaceEnd}}', '')}
    >
      <HeaderText
        className="caption-regular"
        data-testid={dataTestIds.header}
        dangerouslySetInnerHTML={{
          __html: headerText
            .replaceAll('{{tagReplaceStart}}', '<span class="caption-regular">')
            .replaceAll('{{tagReplaceEnd}}', '</span>'),
        }}
      />
    </SmartTooltip>
  ), [dataTestIds.header, headerText]);

  return (
    <CardBody
      data-testid={dataTestIds.card}
      border="dropShadow"
      content={(
        <CardContainer>
          {loading && <LoaderWidget dataTestId={`${dataTestIds.card}-loading`} fullScreen />}
          {!loading && (
            <>
              <HeaderContainer>
                {showCheckmarkCircleIcon && (
                  <IconCheckmarkCircle
                    data-testid={`${dataTestIds.header}-icon`}
                    filled
                    size={20}
                    customStyle={{
                      color: primitives.color.green7,
                    }}
                  />
                )}
                {headerTextComponent}
              </HeaderContainer>
              <ContentContainer>
                <Title
                  className="subtitle-large"
                  data-testid={dataTestIds.title}
                >
                  {title}
                </Title>
                <ImageContainer>
                  {icon}
                </ImageContainer>
                <Description
                  className="caption-regular"
                  data-testid={dataTestIds.description}
                >
                  {description}
                </Description>
                <Footer>
                  <Button
                    data-testid={dataTestIds.link}
                    onClick={onClickHandler}
                    appearance="ghost"
                    className="label-regular "
                  >
                    {footerText}
                  </Button>
                </Footer>
              </ContentContainer>
            </>
          )}
        </CardContainer>
      )}
    />
  );
};

SolutionCardTemplate.defaultProps = {
  analyticsData: null,
  onClick: null,
  footerHyperlink: '',
  showCheckmarkCircleIcon: false,
  loading: false,
};

SolutionCardTemplate.propTypes = {
  analyticsData: PropTypes.shape({
    action: PropTypes.string,
    activity: PropTypes.string,
    screenPath: PropTypes.string,
    screenName: PropTypes.string,
    screenMode: PropTypes.string,
    controlName: PropTypes.string,
    version: PropTypes.string,
  }),
  headerText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  footerHyperlink: PropTypes.string,
  footerText: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  dataTestIds: PropTypes.shape({
    card: PropTypes.string,
    header: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    link: PropTypes.string,
  }).isRequired,
  showCheckmarkCircleIcon: PropTypes.bool,
  loading: PropTypes.bool,
};

export default SolutionCardTemplate;
